import * as React from "react"
import { type Table } from "@tanstack/react-table"
import { X } from "lucide-react"

import { cn } from "../../../utils"
import { Button } from "../button"
import { Separator } from "../separator"

interface FloatingBarAction {
    label: string
    onClick: () => void
    icon?: React.ComponentType<{ className?: string }>
    variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link"
}

interface DataGridFloatingBarProps<TData> {
    table: Table<TData>
    actions?: FloatingBarAction[]
    className?: string
}

export function DataGridFloatingBar<TData>({
    table,
    actions = [],
    className,
}: DataGridFloatingBarProps<TData>) {
    const selectedRows = table.getFilteredSelectedRowModel().rows
    const selectedCount = selectedRows.length

    if (selectedCount === 0) {
        return null
    }

    return (
        <div
            className={cn(
                "fixed bottom-4 left-1/2 z-50 -translate-x-1/2",
                "flex items-center gap-2 rounded-lg border bg-background px-4 py-2 shadow-lg",
                className
            )}
        >
            <span className="text-sm font-medium">
                {selectedCount} row{selectedCount === 1 ? "" : "s"} selected
            </span>

            {actions.length > 0 && (
                <>
                    <Separator orientation="vertical" className="h-6" />
                    <div className="flex items-center gap-1">
                        {actions.map((action, index) => (
                            <Button
                                key={index}
                                variant={action.variant || "secondary"}
                                size="sm"
                                onClick={action.onClick}
                                className="h-7"
                            >
                                {action.icon && (
                                    <action.icon className="mr-1 h-3.5 w-3.5" />
                                )}
                                {action.label}
                            </Button>
                        ))}
                    </div>
                </>
            )}

            <Separator orientation="vertical" className="h-6" />

            <Button
                variant="ghost"
                size="sm"
                onClick={() => table.toggleAllRowsSelected(false)}
                className="h-7 px-2"
            >
                <X className="h-4 w-4" />
                <span className="sr-only">Clear selection</span>
            </Button>
        </div>
    )
}

export type { FloatingBarAction }
